import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Element } from 'react-scroll';
import { useToast } from '@components/common/toast';
import { useDuplicationMobileCheck, useVerificationSmsSend } from '@repository/verification/useMobile';

import styles from '@components/join/join.module.scss';
import LabelInput from '@components/labelInput';
import Token from '@components/join/basicInfo/token';
import CountryCode from '@components/loginModal/find/form/mobile/CountryCode';
import { MobileCertStateType } from '@domain/constant/MobileCertStateType';
import { cellphoneValidator, valueRequired } from '@utils/validators/validators';
import { ERROR_TEXT, SUCCESS_TEXT } from '@utils/validators/text';
import useTimer from '@utils/hooks/useTimer';
import { hasErrorMessage } from '@utils/typeGuard';
import TextFormat from '@utils/text/format';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

interface IMobile {
  prefixName: string;
  mobileCertState: MobileCertStateType | null;
  setMobileCertState: (mobileCertState: MobileCertStateType | null) => void;
}

const Mobile = ({ prefixName, mobileCertState, setMobileCertState }: IMobile) => {
  const {
    watch,
    register,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const name = watch('name');
  const birthDate = watch('birthDate')?.replace(/[^0-9]/g, '');
  const genderFlag = watch('genderFlag');
  const countryCode = watch('countryCode');
  const mobile = watch(prefixName)?.replace(/[^0-9]/g, '');
  const Toast = useToast();

  // 인증 관련 버튼 비활성화/활성화 상태값
  const isDisabledRequestCertBtn =
    !!errors.name ||
    !name ||
    !!errors.birthDate ||
    !birthDate ||
    !!errors.genderFlag ||
    !genderFlag ||
    !!errors.countryCode ||
    !countryCode;

  // 휴대폰 중복 체크
  const { mutateAsync: duplicationCheckMobile } = useDuplicationMobileCheck();
  // SMS 인증 전달
  const { mutateAsync: verificationSmsSend } = useVerificationSmsSend({
    onError: (e) => {
      if (hasErrorMessage(e)) {
        Toast({ type: 'error', iconType: 'info', content: e.response.data.message });
      }
    },
  });

  const { getRemainingTimeFormat, onStart, onStop, remainingTime } = useTimer({
    time: 3 * 60,
    start: false,
    onCountUp: () => setMobileCertState('END'),
  });

  //핸드폰 상태값 초기화
  const onMobileInit = async () => {
    clearErrors(['token', 'mobile']);
    onStart();
    setValue('token', '');
    await onClickRequestCert();
  };

  // 핸드폰 인증
  const onClickRequestCert = async () => {
    //중복 확인
    const rs = await duplicationCheckMobile({ mobile, countryCode });
    //휴대폰 중복이 아닌 경우
    if (!rs) {
      await verificationSmsSend({ birthDate, countryCode, mobile, name });
      setMobileCertState('ING');
      onStart();
    } else {
      setError('mobile', { message: ERROR_TEXT.MOBILE_DUPLICATE });
    }
  };

  const getCertBtnDisable = () => {
    if (isDisabledRequestCertBtn) return true;
    if (!mobile) return true;
    if (mobileCertState === 'SUCCESS' || mobileCertState === 'ING') return true;
    if (errors.mobile?.message === ERROR_TEXT.MOBILE_CERT_REQUIRED) return false;
    return !!errors.mobile;
  };

  useEffect(() => {
    setMobileCertState(null);
    setValue('token', '');
  }, [mobile]);

  const handleClickCountryItem = () => {
    if (errors.mobile?.message === ERROR_TEXT.MOBILE_DUPLICATE) clearErrors('mobile');
  };

  return (
    <>
      <Element name={prefixName} className={cx('infoInner')}>
        <div className={cx('infoWrap')}>
          <CountryCode cx={classnames.bind(styles)} onClickItem={handleClickCountryItem} />
          <LabelInput
            type="text"
            {...register(prefixName, {
              ...valueRequired,
              validate: (value: string) => cellphoneValidator(value.replace(/[^0-9]/g, '')),
            })}
            className={cx('input', 'mobile', { error: errors.mobile, success: mobileCertState === 'SUCCESS' })}
            placeholder="휴대폰 번호"
            maxLength={13}
            onInput={(e: Record<string, any>) => setValue('mobile', TextFormat.hypenMobile(e.target.value))}
            isvalue={mobile}
            tabIndex={3}
          />
          <span className={cx('btnArea')}>
            {mobileCertState === 'ING' && (
              <button
                type="button"
                className={cx('btnInfo')}
                onClick={() => onMobileInit()}
                disabled={remainingTime > 120}
              >
                재인증
              </button>
            )}
            {mobileCertState !== 'ING' && (
              <button
                type="button"
                className={cx('btnInfo')}
                onClick={onClickRequestCert}
                disabled={getCertBtnDisable()}
              >
                인증
              </button>
            )}
          </span>
        </div>
        {!errors.mobile && mobileCertState === 'ING' && (
          <div className={cx('infoText', 'success')}>{SUCCESS_TEXT.MOBILE_CERT_SEND}</div>
        )}
        {errors.mobile && <div className={cx('infoText', 'error')}>{errors.mobile.message as string}</div>}
      </Element>

      {/* [D] 인증번호 영역 */}
      {(mobileCertState === 'ING' || mobileCertState === 'SUCCESS') && (
        <Token
          timerFormat={getRemainingTimeFormat('mm:ss')}
          timerStop={onStop}
          mobileCertState={mobileCertState}
          setMobileCertState={setMobileCertState}
        />
      )}
    </>
  );
};

export default Mobile;
